import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

const STORAGE_KEY = "contacts:selectedShopIds";

const ContactsContext = createContext(null);

const readStoredShopIds = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

export const ContactsProvider = ({ children }) => {
  const shops = useSelector((s) => s.shops?.data || []);
  const auth = useSelector((s) => s.auth);
  const user = auth?.user || null;

  const [selectedShopIds, setSelectedShopIdsState] = useState(() =>
    readStoredShopIds(),
  );
  const [activeGroup, setActiveGroup] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    staffIds: [],
    tagIds: [],
    tagZaloIds: [],
    pipelineStageIds: [],
    categories: [],
    sortMode: "recent",
  });
  const [lookups, setLookups] = useState({
    tagsById: {},
    tagZalosById: {},
    pipelineStagesById: {},
    staffsById: {},
  });

  // Nếu chưa có selectedShopIds (lần đầu) và có shops → chọn shop đầu tiên
  useEffect(() => {
    if (selectedShopIds.length === 0 && shops.length > 0) {
      setSelectedShopIds([shops[0]._id]);
    }
    // Loại bỏ shopId đã không còn thấy (user bị thu quyền)
    if (selectedShopIds.length > 0 && shops.length > 0) {
      const validIds = new Set(shops.map((s) => s._id));
      const filtered = selectedShopIds.filter((id) => validIds.has(id));
      if (filtered.length !== selectedShopIds.length) {
        setSelectedShopIds(filtered);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shops]);

  const setSelectedShopIds = (ids) => {
    setSelectedShopIdsState(ids);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch (e) {
      /* ignore */
    }
  };

  const value = useMemo(
    () => ({
      shops,
      user,
      selectedShopIds,
      setSelectedShopIds,
      activeGroup,
      setActiveGroup,
      search,
      setSearch,
      filters,
      setFilters,
      lookups,
      setLookups,
    }),
    [shops, user, selectedShopIds, activeGroup, search, filters, lookups],
  );

  return (
    <ContactsContext.Provider value={value}>
      {children}
    </ContactsContext.Provider>
  );
};

export const useContacts = () => {
  const ctx = useContext(ContactsContext);
  if (!ctx) {
    throw new Error("useContacts must be used inside <ContactsProvider>");
  }
  return ctx;
};
